/**
 * Value Representation classification, per PS3.5 section 6.2.
 *
 * Every behavioural difference between VRs that the reader or the writer
 * depends on is stated here as data rather than as a branch at the use site,
 * so adding a VR is one table edit and the encoding rules stay auditable
 * against the standard.
 */

/** The 34 standard Value Representations. */
export const VALUE_REPRESENTATIONS = [
    'AE',
    'AS',
    'AT',
    'CS',
    'DA',
    'DS',
    'DT',
    'FD',
    'FL',
    'IS',
    'LO',
    'LT',
    'OB',
    'OD',
    'OF',
    'OL',
    'OV',
    'OW',
    'PN',
    'SH',
    'SL',
    'SQ',
    'SS',
    'ST',
    'SV',
    'TM',
    'UC',
    'UI',
    'UL',
    'UN',
    'UR',
    'US',
    'UT',
    'UV',
] as const;

export type Vr = (typeof VALUE_REPRESENTATIONS)[number];

const VR_SET: ReadonlySet<string> = new Set(VALUE_REPRESENTATIONS);

export function isVr(value: string): value is Vr {
    return VR_SET.has(value);
}

/**
 * VRs encoded in explicit-VR streams as `VR + 2 reserved bytes + 4-byte
 * length`. Everything else uses `VR + 2-byte length`.
 */
const LONG_FORM_VRS: ReadonlySet<Vr> = new Set<Vr>([
    'OB',
    'OD',
    'OF',
    'OL',
    'OV',
    'OW',
    'SQ',
    'SV',
    'UC',
    'UN',
    'UR',
    'UT',
    'UV',
]);

export function usesLongFormLength(vr: Vr): boolean {
    return LONG_FORM_VRS.has(vr);
}

/** Fixed-width binary VRs, mapped to the byte width of one value. */
const BINARY_VALUE_WIDTHS: Readonly<Partial<Record<Vr, number>>> = {
    AT: 4,
    FL: 4,
    FD: 8,
    SL: 4,
    SS: 2,
    SV: 8,
    UL: 4,
    US: 2,
    UV: 8,
};

export function binaryValueWidth(vr: Vr): number | undefined {
    return BINARY_VALUE_WIDTHS[vr];
}

/**
 * VRs whose value is an opaque byte stream — no per-value structure the
 * engine interprets. Their values stay as views over the source buffer.
 */
const BYTE_STREAM_VRS: ReadonlySet<Vr> = new Set<Vr>([
    'OB',
    'OD',
    'OF',
    'OL',
    'OV',
    'OW',
    'UN',
]);

export function isByteStreamVr(vr: Vr): boolean {
    return BYTE_STREAM_VRS.has(vr);
}

/**
 * VRs holding character data.
 *
 * `SQ` is excluded — it holds nested datasets — as are the byte-stream VRs,
 * whose content is not text even when it happens to be printable.
 */
const STRING_VRS: ReadonlySet<Vr> = new Set<Vr>([
    'AE',
    'AS',
    'CS',
    'DA',
    'DS',
    'DT',
    'IS',
    'LO',
    'LT',
    'PN',
    'SH',
    'ST',
    'TM',
    'UC',
    'UI',
    'UR',
    'UT',
]);

export function isStringVr(vr: Vr): boolean {
    return STRING_VRS.has(vr);
}

/**
 * Text VRs, in which backslash is a literal character rather than the value
 * delimiter, so their value multiplicity is always 1 (PS3.5 section 6.2).
 *
 * Splitting one of these on backslash corrupts free-text fields such as
 * StructureSetDescription; the distinction is load-bearing, not cosmetic.
 */
const SINGLE_VALUE_TEXT_VRS: ReadonlySet<Vr> = new Set<Vr>([
    'LT',
    'ST',
    'UR',
    'UT',
]);

export function isMultiValued(vr: Vr): boolean {
    return isStringVr(vr) && !SINGLE_VALUE_TEXT_VRS.has(vr);
}

/**
 * VRs whose interpretation depends on SpecificCharacterSet (PS3.5 section
 * 6.1.2). All other string VRs are restricted to the default repertoire and
 * are always decoded as ASCII, regardless of what (0008,0005) declares.
 */
const CHARACTER_SET_AWARE_VRS: ReadonlySet<Vr> = new Set<Vr>([
    'LO',
    'LT',
    'PN',
    'SH',
    'ST',
    'UC',
    'UT',
]);

export function isCharacterSetAware(vr: Vr): boolean {
    return CHARACTER_SET_AWARE_VRS.has(vr);
}

/**
 * The byte a value of this VR is padded with to reach an even length.
 *
 * UI pads with NUL and the byte-stream VRs pad with NUL; every other string
 * VR pads with a space. Fixed-width binary VRs are inherently even and are
 * never padded, so their entry is unused.
 */
export function paddingByte(vr: Vr): number {
    if (vr === 'UI' || isByteStreamVr(vr)) {
        return 0x00;
    }
    return 0x20;
}

/**
 * Resolves the ambiguous VR codes the standard data dictionary uses for
 * attributes whose representation depends on another attribute's value.
 *
 * The engine reads these as their unsigned variant, which is the correct
 * choice whenever PixelRepresentation is 0 (unsigned) — the overwhelmingly
 * common case, and the same choice dcmjs makes. The resolution is recorded
 * on the element so a write reproduces what was read; a caller that needs
 * the signed interpretation of a specific attribute reads its bytes.
 *
 * Ambiguity only affects value *decoding*: implicit-VR streams carry no VR
 * on the wire, so byte-level round-tripping is unaffected by this choice.
 */
const AMBIGUOUS_VR_RESOLUTIONS: Readonly<Record<string, Vr>> = {
    // US or SS, per PixelRepresentation.
    xs: 'US',
    // OB or OW, per BitsAllocated.
    ox: 'OW',
    // US or SS or OW.
    xw: 'US',
    // US or OW.
    us: 'US',
    // "not applicable" — dataset-less items and delimiters.
    na: 'UN',
};

/**
 * Normalizes a dictionary VR string to a concrete VR.
 *
 * Returns UN for anything unrecognized: an unknown VR is a byte stream of
 * unknown meaning, which is exactly what UN denotes, and it keeps the bytes
 * intact for the write side instead of discarding them.
 */
export function resolveVr(dictionaryVr: string | undefined): Vr {
    if (dictionaryVr === undefined) {
        return 'UN';
    }
    const trimmed = dictionaryVr.trim();
    if (isVr(trimmed)) {
        return trimmed;
    }
    return AMBIGUOUS_VR_RESOLUTIONS[trimmed.toLowerCase()] ?? 'UN';
}
